import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { auth } from "../firebase";
import { authErrorKey } from "../lib/authErrors";
import { card, narrowPageContainer, primaryBtn, inputStyle, errorStyle, pageTitle } from "../styles";

type Status = "verifying" | "valid" | "invalid" | "submitting" | "success";

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.82rem",
  fontWeight: 600,
  color: "var(--color-text-muted)",
  marginBottom: "0.35rem",
};

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  useBreadcrumbs([{ label: t("common.home"), to: "/" }, { label: t("resetPassword.title") }]);
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState<Status>("verifying");
  const [email, setEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const mode = searchParams.get("mode");
    const oobCode = searchParams.get("oobCode");
    if (mode !== "resetPassword" || !oobCode) {
      setStatus("invalid");
      return;
    }
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setStatus("valid");
      })
      .catch((err: any) => {
        setError(t(authErrorKey(err.code)));
        setStatus("invalid");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError(t("resetPassword.errors.minLength")); return; }
    if (newPassword !== confirmPassword) { setError(t("resetPassword.errors.mismatch")); return; }

    const oobCode = searchParams.get("oobCode")!;
    setError(null);
    setStatus("submitting");
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus("success");
    } catch (err: any) {
      setError(t(authErrorKey(err.code)));
      setStatus("valid");
    }
  };

  return (
    <div style={{ ...narrowPageContainer, padding: "2rem 0" }}>
      <div style={card}>
        <h1 style={{ ...pageTitle, marginTop: 0, marginBottom: "1.25rem" }}>{t("resetPassword.title")}</h1>

        {status === "verifying" && (
          <p style={{ color: "var(--color-text-muted)" }}>{t("resetPassword.verifying")}</p>
        )}

        {status === "invalid" && (
          <div>
            <h2 style={{ fontSize: "1.05rem", margin: "0 0 0.5rem" }}>{t("resetPassword.invalidTitle")}</h2>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.25rem" }}>{t("resetPassword.invalidBody")}</p>
            <Link to="/" style={{ ...primaryBtn, textDecoration: "none", display: "inline-block" }}>
              {t("resetPassword.backToSignIn")}
            </Link>
          </div>
        )}

        {(status === "valid" || status === "submitting") && (
          <form onSubmit={handleSubmit}>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.25rem", fontSize: "0.9rem", lineHeight: 1.6 }}>
              {t("resetPassword.instructions", { email })}
            </p>
            <label style={labelStyle}>{t("resetPassword.newPassword")}</label>
            <input
              type="password"
              aria-label={t("resetPassword.newPassword")}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setError(null); }}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "0.85rem" }}
              required
              autoFocus
            />
            <label style={labelStyle}>{t("resetPassword.confirmPassword")}</label>
            <input
              type="password"
              aria-label={t("resetPassword.confirmPassword")}
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(null); }}
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", marginBottom: "1rem" }}
              required
            />
            {error && <p style={{ ...errorStyle, marginBottom: "1rem" }}>{error}</p>}
            <button type="submit" disabled={status === "submitting"} style={{ ...primaryBtn, width: "100%" }}>
              {status === "submitting" ? t("resetPassword.submitting") : t("resetPassword.submit")}
            </button>
          </form>
        )}

        {status === "success" && (
          <div>
            <p style={{ marginBottom: "0.35rem", fontWeight: 600 }}>{t("resetPassword.success")}</p>
            <p style={{ color: "var(--color-text-muted)", marginBottom: "1.25rem" }}>{t("resetPassword.successBody")}</p>
            <Link to="/" style={{ ...primaryBtn, textDecoration: "none", display: "inline-block" }}>
              {t("resetPassword.goHome")}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
